import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import "@/index.css";
import App from "@/App";
import { installApiBaseFetchShim } from "@/utils/installApiBaseFetchShim";
import { captureAttributionOnce } from "@/utils/signupAttribution";
import { captureOpenAIAdsAttribution } from "@/utils/openaiAdsAttribution";
import { safeSession } from '@/utils/safeStorage';

// Admin Visibility Phase 1.2 — capture UTMs + referrer on first page load.
captureAttributionOnce();
captureOpenAIAdsAttribution();
installApiBaseFetchShim();

// Seed the window-scoped session id so authStore (and anyone else) can read it
// before useTracking's first render. sessionStorage can throw SecurityError
// in private/incognito mode (Brave, Safari) — swallow so the app still mounts.
if (typeof window !== 'undefined') {
  let sid: string | null = null;
  try { sid = safeSession.get('ts_session_id'); } catch { /* private mode */ }
  if (!sid) {
    sid = `s_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    try { safeSession.set('ts_session_id', sid); } catch { /* private mode */ }
  }
  (window as { __ts_session_id?: string }).__ts_session_id = sid;
}

// Initialize Sentry for error tracking
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: window.location.hostname.includes('preview') ? 'preview' : 'production',
    
    // Capture 100% of errors, 10% of transactions for performance
    tracesSampleRate: 0.1,
    
    // Filter out noisy errors (these are harmless/expected)
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      /ResizeObserver/,
      'Non-Error promise rejection',
      /Loading chunk \d+ failed/,
      // PostHog/rrweb cross-origin iframe errors (harmless)
      /Blocked a frame with origin.*from accessing a cross-origin frame/,
      /removeEventListener.*cross-origin/,
      /SecurityError.*cross-origin frame/,
      // Common browser extensions
      /chrome-extension/,
      /moz-extension/,
    ],
    
    // Add user context when available
    beforeSend(event) {
      // Skip PostHog/rrweb errors entirely
      if (event.exception?.values?.some(e => 
        e.value?.includes('rrweb') || 
        e.value?.includes('posthog') ||
        e.stacktrace?.frames?.some(f => f.filename?.includes('rrweb'))
      )) {
        return null;
      }
      
      // Add device info
      event.tags = {
        ...event.tags,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        isMobile: window.innerWidth < 768 ? 'yes' : 'no',
      };
      return event;
    },
  });
}

// Suppress PostHog/rrweb SecurityError from React dev overlay
window.addEventListener('error', (event) => {
  if (event.message?.includes('SecurityError') && 
      event.message?.includes('removeEventListener')) {
    event.preventDefault();
  }
});

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// Service worker: register in PRODUCTION only. In development the cached SW
// kept serving stale builds, so we simply don't register it in dev.
if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  // Auto-update: when a freshly-deployed SW activates (it calls skipWaiting +
  // clients.claim), the controller changes — reload once so the page runs the
  // new assets instead of a stale cached app. Guard against the first-ever
  // install (no prior controller) so new visitors don't get a needless reload.
  const hadController = !!navigator.serviceWorker.controller;
  let refreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing || !hadController) return;
    refreshing = true;
    window.location.reload();
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js').then((reg) => {
      // Proactively check for a new SW now and whenever the tab regains focus,
      // so users pick up deploys promptly instead of on the browser's lazy schedule.
      reg.update().catch(() => {});
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') reg.update().catch(() => {});
      });
    }).catch(() => {});
  });
} else if ('serviceWorker' in navigator) {
  // Dev: a service worker left over from a prior production/local run keeps
  // serving the stale cached app on localhost (dev registers no SW of its own).
  // Proactively unregister any SW + clear its caches so dev always reflects the
  // latest build. Reload once if an active SW was actually controlling the page.
  navigator.serviceWorker.getRegistrations().then((regs) => {
    const hadActive = !!navigator.serviceWorker.controller;
    Promise.all(regs.map((r) => r.unregister())).then(() => {
      if (window.caches) {
        caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k)))).catch(() => {});
      }
      if (hadActive) window.location.reload();
    });
  }).catch(() => {});
}
